import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, FileDown, Check } from 'lucide-react';
import { useOrder } from '../context/OrderContext';
import { computePrice, fmt } from '../lib/pricing';
import { validateStep, validateAll, toPayload, minStartDate } from '../lib/orderValidation';
import { scrollTo } from '../lib/scroll';
import { EASE } from '../lib/motion';
import SectionHead from './SectionHead';
import OrderSummary from './OrderSummary';
import { StepSelection, StepOptions, StepStart, StepContact, StepSign } from './OrderSteps';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ['Auswahl', 'Laufzeit & Extras', 'Start & Spot', 'Ihre Daten', 'Unterschrift'];

function Stepper({ step, onJump }) {
  return (
    <ol className="grid grid-cols-5 gap-2" data-testid="order-stepper">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <li key={label}>
            <button
              type="button"
              data-testid={`stepper-step-${n}`}
              onClick={() => done && onJump(n)}
              aria-current={active ? 'step' : undefined}
              className={`group w-full text-left ${done ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <span className={`block h-1 rounded-full transition-colors duration-300 ${active || done ? 'bg-[#FF6B00]' : 'bg-[#282C37]'}`} />
              <span className="mt-3 flex items-center gap-2">
                <span className={`flex h-5 w-5 items-center justify-center rounded-full font-mono-num text-[10px] font-bold ${
                  done ? 'bg-[#FF6B00] text-white' : active ? 'border border-[#FF6B00] text-[#FF6B00]' : 'border border-[#282C37] text-[#7D8391]'
                }`}>
                  {done ? <Check className="h-3 w-3" /> : n}
                </span>
                <span className={`hidden text-xs font-semibold sm:inline ${active ? 'text-white' : done ? 'text-[#B6BCC8] group-hover:text-white' : 'text-[#7D8391]'}`}>{label}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export default function OrderForm() {
  const { order, update, resetOrder } = useOrder();
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const price = computePrice(order);
  const minDate = minStartDate();

  const validate = (s) => validateStep(order, s, minDate);

  const goTo = (n) => {
    setErrors({});
    setStep(n);
    scrollTo('#auftrag');
  };

  const next = () => {
    const e = validate(step);
    setErrors(e);
    if (Object.keys(e).length > 0) return toast.error('Bitte prüfen Sie die markierten Felder.');
    goTo(Math.min(5, step + 1));
  };

  const submit = async () => {
    const all = validateAll(order, minDate);
    setErrors(all);
    if (Object.keys(all).length > 0) return toast.error('Bitte prüfen Sie Ihre Angaben.');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/orders`, toPayload(order));
      setResult(res.data);
      toast.success('Ihre Anfrage ist eingegangen.');
      scrollTo('#auftrag');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Senden fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <section id="auftrag" data-testid="order-section" className="chapter">
        <div className="mx-auto max-w-2xl px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="panel p-9 sm:p-12" data-testid="order-confirmation">
            <CheckCircle2 className="h-14 w-14 text-emerald-400" />
            <p className="eyebrow mt-8">Eingegangen</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Danke – Ihre Anfrage ist bei uns.</h2>
            <p className="t-body mt-4">{result.message}</p>
            <div className="mt-8 rounded-xl border border-[#282C37] bg-[#0F1012] p-6">
              <p className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#7D8391]">Ihre Vorgangsnummer</p>
              <p className="mt-1 font-mono-num text-sm font-bold text-[#FF6B00]" data-testid="order-reference">{result.id}</p>
              <div className="mt-5 flex justify-between text-sm"><span className="text-[#B6BCC8]">Gesamt netto</span><span className="font-mono-num font-bold" data-testid="order-confirmation-netto">{fmt(result.totalNetto)}</span></div>
              <div className="mt-1.5 flex justify-between text-sm"><span className="text-[#B6BCC8]">Gesamt brutto (inkl. 19 % USt.)</span><span className="font-mono-num" data-testid="order-confirmation-brutto">{fmt(result.totalBrutto)}</span></div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`${process.env.REACT_APP_BACKEND_URL}${result.pdfUrl}`} download data-testid="order-download-pdf" className="btn-primary !px-6 !py-3">
                <FileDown className="h-4 w-4" /> Anfrage als PDF
              </a>
              <button type="button" data-testid="order-new-request-button" onClick={() => { resetOrder(); setResult(null); setStep(1); }} className="btn-ghost !px-6 !py-3">
                Neue Anfrage stellen
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="auftrag" data-testid="order-section" className="chapter">
      <div className="wrap">
        <SectionHead
          n="09"
          eyebrow="Auftrag"
          title="Ihr Auftrag – in fünf Schritten."
          testid="order-headline"
          lead="Paket wählen, Laufzeit festlegen, Daten eintragen, unterschreiben, absenden. Den Preis sehen Sie jederzeit in der Zusammenfassung – ohne versteckte Kosten."
        />

        <div className="mt-16 grid gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <Stepper step={step} onJump={goTo} />
            <div className="panel mt-8 p-6 sm:p-9">
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35, ease: EASE }}>
                  {step === 1 && <StepSelection order={order} update={update} errors={errors} />}
                  {step === 2 && <StepOptions order={order} update={update} />}
                  {step === 3 && <StepStart order={order} update={update} errors={errors} minDate={minDate} />}
                  {step === 4 && <StepContact order={order} update={update} errors={errors} />}
                  {step === 5 && <StepSign order={order} update={update} errors={errors} />}
                </motion.div>
              </AnimatePresence>

              <div className="mt-10 flex items-center justify-between gap-4 border-t border-[#282C37] pt-6">
                <button type="button" data-testid="order-back-button" onClick={() => goTo(Math.max(1, step - 1))} disabled={step === 1} className="btn-ghost !px-5 !py-3 disabled:cursor-not-allowed disabled:opacity-40">
                  <ArrowLeft className="h-4 w-4" /> Zurück
                </button>
                {step < 5 ? (
                  <button type="button" data-testid="order-next-button" onClick={next} className="btn-primary !px-6 !py-3">
                    Weiter <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button type="button" data-testid="order-submit-button" onClick={submit} disabled={submitting} className="btn-primary !px-6 !py-3 disabled:opacity-60">
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {order.kind === 'beratung' ? 'Beratung anfragen' : 'Auftrag verbindlich anfragen'}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-4">
            <OrderSummary order={order} price={price} step={step} />
          </div>
        </div>
      </div>
    </section>
  );
}
